import { Copy, SquareArrowOutUpRight } from 'lucide-react'
import React, { type Dispatch, type SetStateAction, useEffect, useState } from 'react'

import { adminRecordInspector } from 'rails_routes'
import { Banner } from 'slab'
import { getDropdownCoverageAuditReasons } from 'utils/brokers/coverageAuditReasonUtils'
import { type BrokersEnrollmentEnriched } from 'utils/web_api.schemas'

import EditEnrollmentBrokerData from './EditEnrollmentBrokerData'
import EditEnrollmentCoverages from './EditEnrollmentCoverages'
import EditEnrollmentData from './EditEnrollmentData'
import EditEnrollmentInsurancePremiums from './EditEnrollmentInsurancePremiums'
import EnrollmentAuditHistory from './EnrollmentAuditHistory'
import { ReasonHelpLink } from './ReasonHelpModal'
import CoverageAuditReasonContext from '../context/CoverageAuditReasonContext'
import Badge from '../reusable/Badge'
import CopyButtonWithAnimation from '../reusable/CopyButtonWithAnimation'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../ui/select'
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from '../ui/sheet'

// Available reasons from the Insurance::CoverageAuditLog::Reason enum
// This is automatically synced with the Rails enum via the generated TypeScript types
const COVERAGE_AUDIT_REASONS = getDropdownCoverageAuditReasons()

// EditEnrollmentSheet is a component that allows the user to edit an enrollment via a sheet
// that slides in from the right of the screen.
function EditEnrollmentSheet ({
  isOpen,
  enrollment,
  setIsOpen,
}: {
  isOpen: boolean
  enrollment: BrokersEnrollmentEnriched
  setIsOpen: Dispatch<SetStateAction<boolean>>
  memberId: string
}) {
  const [reason, setReason] = useState<string>('')
  const [reasonSelectOpen, setReasonSelectOpen] = useState(false)
  const [showReasonWarning, setShowReasonWarning] = useState(false)

  // Reset reason to undefined whenever the sheet opens
  useEffect(() => {
    if (isOpen) {
      setReason('')
      setReasonSelectOpen(false)
      setShowReasonWarning(false)
    }
  }, [isOpen])

  const openReasonDropdown = () => {
    setReasonSelectOpen(true)
    setShowReasonWarning(true)
  }

  const handleReasonChange = (newReason: string) => {
    setReason(newReason)
    setShowReasonWarning(false) // Hide warning when reason is selected
  }

  const handleOpenChange = (open: boolean) => {
    setReasonSelectOpen(open)
    if (!open) {
      setShowReasonWarning(false) // Hide warning when dropdown is closed
    }
  }

  return (
    <Sheet open={isOpen} onOpenChange={setIsOpen}>
      <SheetContent
        className='h-screen flex flex-col'
        style={{ minWidth: '720px' }}
      >
        <div className='border-b border-gray-200 pb-2'>
          <SheetHeader>
            <SheetTitle className='text-left'>
              <div className='text-2xl font-normal mb-2'>Edit Enrollment</div>

              <div className='space-y-1 text-sm'>
                <div className='flex items-center gap-2'>
                  <span className='text-gray-600'>ID:</span>
                  <span className='font-light'>{enrollment.id}</span>
                  <SquareArrowOutUpRight
                    className='cursor-pointer text-gray-500 hover:text-gray-700 transition-colors'
                    size={16}
                    onClick={() => window.open(adminRecordInspector.show({ id: enrollment.id }))}
                  />
                  <CopyButtonWithAnimation content={enrollment.id}>
                    <Copy className='text-gray-500 hover:text-gray-700 transition-colors' size={16} />
                  </CopyButtonWithAnimation>
                </div>

                <div className='flex items-center gap-2'>
                  <span className='text-gray-600'>Member:</span>
                  <span className='font-light'>{enrollment.formatted_member_name}</span>
                </div>

                <div className='flex items-center gap-2'>
                  <span className='text-gray-600'>Type:</span>
                  <span className='font-light'>{enrollment.coverage}</span>
                </div>

                <div>
                  {enrollment.reimbursement_only
                    ? (
                      <Badge text='Reimbursement Only' type='attention' />
                      )
                    : (
                      <Badge text='Not Reimbursement Only' type='default' />
                      )}
                </div>
              </div>
            </SheetTitle>

            <SheetDescription className='text-left mt-4 mb-6'>
              Edit the enrollment details below. All changes require a reason for audit tracking.
            </SheetDescription>

            <div className='space-y-2'>
              <div className='flex items-center justify-between'>
                <label className='block text-sm font-medium text-gray-700'>
                  Change Reason <span className='text-red-500'>*</span>
                </label>
                <ReasonHelpLink />
              </div>
              {showReasonWarning && (
                <Banner type='warning' title='Please select a reason'>
                  A reason is required to make this change
                </Banner>
              )}
              {reason === 'member_initiated_change' && (
                <Banner type='warning' title='Member initiated changes are rare'>
                  Please confirm whether the member made this change due to a qualifying life event first. Most member-requested changes are actually qualifying life events.
                </Banner>
              )}
              <Select
                value={reason}
                onValueChange={handleReasonChange}
                open={reasonSelectOpen}
                onOpenChange={handleOpenChange}
              >
                <SelectTrigger className='w-full'>
                  <SelectValue placeholder='Select a reason for this change' />
                </SelectTrigger>
                <SelectContent>
                  {COVERAGE_AUDIT_REASONS.map((reasonOption) => (
                    <SelectItem key={reasonOption.value} value={reasonOption.value}>
                      {reasonOption.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </SheetHeader>
        </div>

        <CoverageAuditReasonContext.Provider value={{ reason, setReason: handleReasonChange, openReasonDropdown, showReasonWarning }}>
          <div className='flex-1 overflow-y-auto pt-1'>
            <div className='space-y-2 pb-4'>
              <EditEnrollmentData enrollment={enrollment} />
              <EditEnrollmentBrokerData enrollment={enrollment} />
              <EditEnrollmentCoverages enrollment={enrollment} />
              <EditEnrollmentInsurancePremiums enrollment={enrollment} />
              <EnrollmentAuditHistory enrollmentId={enrollment.id} />
            </div>
          </div>
        </CoverageAuditReasonContext.Provider>
      </SheetContent>
    </Sheet>
  )
}

export default EditEnrollmentSheet
